import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faBuilding,
  faChartLine,
  faLocationDot,
  faDoorOpen,
  faUsers,
  faLayerGroup,
  faCog,
  faMagnifyingGlass,
  faSparkles,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import {
  navigationIds,
  NavigationIds,
} from '@robinpowered/dashboard-apps-common';
import { config } from 'config';

const ICON_WIDTH = 16;

// Key for the global search entry. It has no route — clicking it opens the
// search overlay (handled in NavSidebar via onOpenSearch).
export const SEARCH_MENU_KEY = 'search';

// Key for the Robin Assistant entry. It has no route — clicking it toggles
// the shell-hosted agent chat sidebar (handled in NavSidebar). Only shown
// when config.agentChatUrl is set (see useNavigation's visibility checks).
export const AGENT_CHAT_MENU_KEY = 'agentChat';

// Navigation item types
export type NavigationItem = {
  id: NavigationIds;
  key: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  route: string | null;
  children?: NavigationItem[];
  section?: 'workplace' | 'schedule';
  matchMode?: 'exact' | 'inclusive';
  className?: string;
};

// Main navigation items
export const MAIN_NAVIGATION: NavigationItem[] = [
  {
    key: SEARCH_MENU_KEY,
    icon: <FontAwesomeIcon icon={faMagnifyingGlass} width={ICON_WIDTH} />,
    label: 'Search',
    route: null,
    id: navigationIds.SEARCH_NAV_LINK,
    // Adds the visual divider between search and the route navigation items.
    className: 'nav-search-item',
  },
  {
    key: AGENT_CHAT_MENU_KEY,
    icon: <FontAwesomeIcon icon={faSparkles} width={ICON_WIDTH} />,
    label: 'Assistant',
    route: null,
    // TODO: promote to navigationIds in @robinpowered/dashboard-apps-common
    // (types/navTypes.ts) alongside a common rebuild.
    id: 'agent-chat-nav-link' as NavigationIds,
  },
  {
    key: 'map',
    icon: <FontAwesomeIcon icon={faLocationDot} width={ICON_WIDTH} />,
    label: 'Map',
    route: '/officeV2',
    id: navigationIds.MAP_NAV_LINK,
  },
  {
    key: 'mySchedule.title',
    icon: <FontAwesomeIcon icon={faCalendar} width={ICON_WIDTH} />,
    label: 'Schedule',
    route: '/schedule/week',
    section: 'schedule',
    id: navigationIds.SCHEDULE_NAV_LINK,
  },
  {
    key: 'people',
    icon: <FontAwesomeIcon icon={faUsers} width={ICON_WIDTH} />,
    label: 'People',
    route: '/people',
    id: navigationIds.PEOPLE_NAV_LINK,
    matchMode: 'exact',
  },
  {
    key: 'meetingSpaces',
    icon: <FontAwesomeIcon icon={faDoorOpen} width={ICON_WIDTH} />,
    label: 'Meeting spaces',
    route: '/schedule/meeting-rooms',
    id: navigationIds.MEETING_ROOMS_NAV_LINK,
  },
  {
    key: 'analytics',
    icon: <FontAwesomeIcon icon={faChartLine} width={ICON_WIDTH} />,
    label: 'Analytics',
    route: '/analytics',
    id: navigationIds.ANALYTICS_NAV_LINK,
  },
  {
    key: 'workplace.title',
    icon: <FontAwesomeIcon icon={faBuilding} width={ICON_WIDTH} />,
    label: 'Workplace',
    route: null,
    id: navigationIds.WORKWEEK_NAV_LINK,
    section: 'workplace',
    children: [
      {
        key: 'workplace.dashboard',
        icon: null,
        label: 'Dashboard',
        route: '/workplace-dashboard',
        id: navigationIds.WORKPLACE_DASHBOARD_NAV_LINK,
      },
      {
        key: 'workplace.tickets',
        icon: null,
        label: 'Tickets',
        route: '/tickets',
        id: navigationIds.TICKETS_NAV_LINK,
      },
      {
        key: 'workplace.visitors',
        icon: null,
        label: 'Visitors',
        route: '/visits',
        id: navigationIds.VISITS_NAV_LINK,
      },
      {
        key: 'workplace.deliveries',
        icon: null,
        label: 'Deliveries',
        route: '/deliveries',
        id: navigationIds.DELIVERIES_NAV_LINK,
      },
    ],
  },
  {
    key: 'spaceManagement.title',
    icon: <FontAwesomeIcon icon={faLayerGroup} width={ICON_WIDTH} />,
    label: 'Space management',
    route: null,
    id: navigationIds.SPACE_MANAGEMENT_NAV_LINK,
    section: 'workplace',
    children: [
      {
        key: 'spaceManagement.scenarioPlanning',
        icon: null,
        label: 'Scenario planning',
        route: '/workplace-management',
        id: navigationIds.SCENARIO_PLANNING_NAV_LINK,
        matchMode: 'exact',
      },
      {
        key: 'spaceManagement.layoutTool',
        icon: null,
        label: 'Layout tool',
        route: '/workplace-management/layout',
        id: navigationIds.LAYOUT_TOOL_NAV_LINK,
      },
    ],
  },
  {
    key: 'manage',
    icon: <FontAwesomeIcon icon={faCog} width={ICON_WIDTH} />,
    label: 'Manage',
    route: `${config.dashboardUrl}/:orgSlug/settings/offices`,
    id: navigationIds.MANAGE_NAV_LINK,
    matchMode: 'exact',
  },
  {
    key: 'manageBeta',
    icon: <FontAwesomeIcon icon={faCog} width={ICON_WIDTH} />,
    label: 'Manage',
    route: '/setup/settings',
    id: navigationIds.MANAGE_BETA_NAV_LINK,
    matchMode: 'inclusive',
  },
];
